import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { stackedBarPairedPalette } from "../colors";
import D3StackedBar from "./D3StackedBar";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedStage,
} from "../../util/customFunctions";
import RccCard from "../RccCard";
import { fetchFirstLevelTeamNames } from "../rccUtils";

/**
 * Displays open pipeline mix by team with parent-team rollup.
 *
 * Strategy: Instead of filtering returned data, we fetch data once per first-level team.
 * When we filter by a parent team name, the API returns all child teams under that parent.
 * We then sum all child-team ACV values into a single parent-team total per quarter.
 *
 * Inputs: Redux state (company), team hierarchy from generic scorecard filters.
 * Outputs: Stacked bar chart of pipeline ACV by first-level team (with child teams rolled up) and quarter.
 */
function PipelineMixByTeamCard({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  // First-level teams are the top-level parent teams in the hierarchy (e.g., "Enterprise", "SMB")
  // We'll fetch data for each of these separately to get their child-team totals
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);

  // Filters applied to the API request (timeframe, stage, type, solution category, etc.)
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Aggregated chart data: [{ xAxisLabel: "2025-Q1", "Team A": 1000, "Team B": 2000, ... }, ...]
  // Each team value is the sum of all child teams under that parent
  const [teamRollupChartData, setTeamRollupChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const company = useSelector((state) => state.app.company);
  const user = useSelector((state) => state.app.user || {});

  /**
   * Step 1: Fetch the list of first-level (parent) team names from the team hierarchy.
   * We need this before we can make per-team API requests.
   * Uses the same source as other RCC cards (Linearity, Pipeline Coverage) for consistency.
   */
  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
  }, []);

  /**
   * Step 2: Fetch Pipeline Mix data for each first-level team separately.
   *
   * Why per-team requests? When we filter by a parent team name (e.g., "Enterprise"),
   * the API returns all child teams under that parent. By fetching once per parent,
   * we can sum all child-team ACV values into a single parent total per quarter.
   *
   * Flow:
   * 1. Wait for first-level team names to load
   * 2. Fetch available filters (timeframe, stage, type, solution category, etc.)
   * 3. Build base filters (excluding team filter)
   * 4. Make parallel API requests: one per first-level team (with team filter)
   * 5. Aggregate results: sum child-team ACV into parent totals per quarter
   */
  useEffect(() => {
    // Don't start fetching until we know which teams to roll up
    if (!isFirstLevelTeamsLoaded) return;

    // Prevent duplicate fetches (e.g., if component re-renders during fetch)
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);

    dispatch({ type: "get_pipeline_mix_filters_request" });

    appService.getPipelineMixAnalysisFilters().then(
      (resp) => {
        // ---- Normalize response ----
        // Flatten the lkpFilters object structure into an array
        const lkpFilters = Object.values(
          resp.message.lkpFilters.reduce((r, c) => Object.assign(r, c), {})
        );

        const defaultTimeFrame = resp.message.defaultTimeFrame;
        const computedFilters = resp.message.computedFilters;

        dispatch({
          type: "get_pipeline_mix_filters_success",
          json: {
            message: computedFilters,
            lkpFilters,
            defaultTimeFrame,
          },
        });

        dispatch({ type: "get_pipeline_mix_analysis_data_request" });

        // ---- Default stage filter ----
        const stageSequence = getDefaultSelectedStage(
          computedFilters.filter((f) => f.type === "stage")
        );

        const defaultStages = computedFilters
          .filter((item) => item.sequence >= stageSequence)
          .map((i) => i.stage_name)
          .filter(Boolean);

        // ---- Build default filters (excluding team filter) ----
        // These filters apply to all requests (timeframe, stage, type, solution category, etc.)
        // We'll add the team filter separately for each first-level team
        let filters = [];

        // ---- Timeframe filter ----
        const tfs = getDefaultSalesType(
          computedFilters.filter((i) => i.type === defaultTimeFrame),
          defaultTimeFrame
        );

        if (tfs[0] !== "All") {
          filters.push({
            name: defaultTimeFrame,
            value: tfs,
            string: true,
          });
        }

        // Add Stage filter if available
        if (defaultStages.length > 0) {
          filters.push({
            name: "stage",
            string: true,
            value: defaultStages,
          });
        }

        // ---- Other defaults ----
        const defaultOppType = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Type"),
          "Type"
        );

        const defaultSolutionCat = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );

        // ---- Company-specific logic ----
        // Some companies have additional filter requirements
        if (company === "C0008") {
          const salesFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "cross_sell1__c"),
            "cross_sell1__c"
          );

          const dealsFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "Type"),
            "Type"
          );

          if (salesFilters[0] !== "All") {
            filters.push({
              name: "cross_sell1__c",
              value: salesFilters,
            });
          }

          if (dealsFilters[0] !== "All") {
            filters.push({
              name: "Type",
              value: dealsFilters,
            });
          }
        }

        if (defaultOppType[0] !== "All") {
          filters.push({ name: "Type", value: defaultOppType });
        }

        if (defaultSolutionCat[0] !== "All") {
          filters.push({ name: "Solution_Cat", value: defaultSolutionCat });
        }

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });

        // Base filters that will be used for all team requests
        const baseFilters = computedFilters.length === 0 ? [] : filters;

        // Determine which teams to fetch data for
        // If we have first-level teams, fetch one request per team
        // Otherwise, fetch once without team filter (fallback)
        const teamsToRollup =
          Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
            ? firstLevelTeamNames
            : null;

        /**
         * Step 3: Create parallel API requests - one per first-level team.
         *
         * For each team:
         * - Add a team filter: { name: "team", value: [teamName], string: true }
         * - The API will return all child teams under this parent
         * - We'll sum all child-team ACV values into the parent total
         */
        const requests = (teamsToRollup || ["__ALL__"]).map((teamName) => {
          // Build team filter: when filtering by parent team, API returns all child teams
          const teamFilter =
            teamName === "__ALL__"
              ? null // No team filter = get all teams (fallback case)
              : { name: "team", value: [teamName], string: true };

          // Combine base filters with team filter
          const requestFilters = teamFilter
            ? [...baseFilters, teamFilter]
            : baseFilters;

          // Make API request and attach teamName to response for aggregation
          return appService
            .getPipelineMixAnalysisData(requestFilters, defaultTimeFrame)
            .then(
              (dataResp) => ({ teamName, dataResp }),
              (error) => ({ teamName, error })
            );
        });

        /**
         * Step 4: Aggregate results from all team requests.
         *
         * For each first-level team's response:
         * - The API returns rows for all child teams under that parent
         * - We sum all child-team ACV values into a single parent total per quarter
         * - Result: quarterTotals[quarter][parentTeam] = sum of all child ACV
         */
        Promise.all(requests).then((results) => {
          // Structure: Map<quarter, { xAxisLabel: quarter, "Team A": totalAcv, "Team B": totalAcv, ... }>
          const quarterTotals = new Map();
          const categories = []; // List of first-level team names for chart categories

          // Process each team's response
          results.forEach(({ teamName, dataResp, error }) => {
            // Skip failed requests
            if (error || !dataResp) return;

            // PipelineMix API returns an object: { message: { data: [...] , ... } }.
            // Redux stores: pipelineMixAnalysisData = json.message (an object), and the blocks live in `.data`.
            // We only care about the "Team" block inside that blocks array.
            const message = dataResp?.message;
            const blocks = Array.isArray(message?.data)
              ? message.data
              : Array.isArray(message)
              ? message
              : Array.isArray(dataResp)
              ? dataResp
              : [];
            const teamDataObj = blocks.find(
              (item) =>
                item.type === "Team" ||
                (item.data &&
                  item.data.length > 0 &&
                  item.data[0].Team !== undefined)
            );
            if (!teamDataObj || !Array.isArray(teamDataObj.data)) return;

            // Determine which field contains the quarter (varies by timeframe)
            const quarterField =
              teamDataObj.timeline || "projected_close_fiscal_quarter";
            const categoryName = teamName === "__ALL__" ? "All" : teamName;

            // Track this team as a category (skip "__ALL__" placeholder)
            if (teamName !== "__ALL__") categories.push(categoryName);

            /**
             * Aggregate child-team ACV into parent total.
             *
             * When we filtered by parent team "Enterprise", the API returned rows like:
             *   { Team: "Enterprise - Sales", quarter: "2025-Q1", acv: 100 }
             *   { Team: "Enterprise - Support", quarter: "2025-Q1", acv: 200 }
             *
             * We sum all these into: quarterTotals["2025-Q1"]["Enterprise"] = 300
             */
            teamDataObj.data.forEach((row) => {
              const quarter = row?.[quarterField];
              if (!quarter) return;

              // Initialize quarter object if it doesn't exist
              if (!quarterTotals.has(quarter)) {
                quarterTotals.set(quarter, { xAxisLabel: quarter });
              }

              // Accumulate ACV for this parent team in this quarter
              const acc = quarterTotals.get(quarter);
              acc[categoryName] = (acc[categoryName] || 0) + (row.acv || 0);
            });
          });

          // Sort quarters chronologically (e.g., "2025-Q1", "2025-Q2", ...)
          const sortedQuarters = Array.from(quarterTotals.values()).sort(
            (a, b) => a.xAxisLabel.localeCompare(b.xAxisLabel)
          );

          /**
           * Step 5: Normalize data for D3 stacked bar chart.
           *
           * D3 expects every quarter to have every category, even if value is 0.
           * Example structure:
           * [
           *   { xAxisLabel: "2025-Q1", "Team A": 1000, "Team B": 0, "Team C": 500 },
           *   { xAxisLabel: "2025-Q2", "Team A": 0, "Team B": 2000, "Team C": 300 }
           * ]
           */
          const finalCategories = categories.length > 0 ? categories : ["All"];
          const normalizedData = sortedQuarters.map((q) => {
            const out = { ...q };
            // Ensure every category exists in every quarter (fill missing with 0)
            finalCategories.forEach((c) => {
              if (out[c] == null) out[c] = 0;
            });
            return out;
          });

          setTeamRollupChartData(normalizedData);
          setIsLoading(false);
          fetchingRef.current = false;
        });
      },
      (error) => {
        fetchingRef.current = false;
        setIsLoading(false);
        dispatch({
          type: "get_pipeline_mix_filters_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      }
    );
  }, [company, dispatch, firstLevelTeamNames, isFirstLevelTeamsLoaded]);

  /**
   * Step 6: Transform aggregated data for D3 stacked bar chart.
   *
   * Extract categories (team names) from the data and sort them according to
   * the team hierarchy order (if available) for consistent display.
   */
  const { chartData, derivedCategories } = useMemo(() => {
    const data = Array.isArray(teamRollupChartData) ? teamRollupChartData : [];
    if (data.length === 0) return { chartData: [], derivedCategories: [] };

    // Extract team names from the first quarter's data (all quarters have same categories)
    const categories = Object.keys(data[0] || {}).filter(
      (k) => k !== "xAxisLabel"
    );

    // Calculate total ACV for each category to sort by total value
    const categoryTotals = {};
    categories.forEach((category) => {
      categoryTotals[category] = data.reduce(
        (sum, row) => sum + (row[category] || 0),
        0
      );
    });

    // Sort categories by total ACV (descending)
    categories.sort((a, b) => categoryTotals[b] - categoryTotals[a]);

    return { chartData: data, derivedCategories: categories };
  }, [teamRollupChartData]);

  const handleZoom = () => {
    // Add logic to open modal
    console.log("Zooming card...");
  };

  return (
    <RccCard
      title={title || "Qualified Pipeline Mix by Team"}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Open Pipeline Mix Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"future"}
    >
      <D3StackedBar
        data={chartData}
        categories={derivedCategories}
        colorPalette={stackedBarPairedPalette}
        currencySymbol={user?.Currency}
        options={{
          showLabels: true,
          showLegend: true,
        }}
      />
    </RccCard>
  );
}

export default PipelineMixByTeamCard;
